import { useCallback, useEffect, useMemo, useRef, useState } from 'react';

import { Button } from '@/lib/base/buttons/button';
import { LoadingIndicator } from '@/lib/base/foundations/loading-indicator';
import { useUpdateCrmProperty } from '@/lib/crm/hooks/useUpdateCrmProperty';
import { useCrmObject } from '@/lib/crm/store';
import { logger } from '@/lib/logger';
import PlateEmailEditor from '@/lib/reasoning/components/email-editor/PlateEmailEditor';
import {
  unwrapSignatureFromEditor,
  wrapSignatureForEditor,
} from '@/lib/reasoning/components/email-editor/signature-opaque';
import type { CrmObject } from '@/types/crm';

export interface AccountSignatureEditorProps {
  orgId: string;
  /** The integration entity ID (e.g. outlook_mail_entity_id). */
  entityId: string;
  userId: number;
  canModify: boolean;
}

const ENTITY_TYPE = 'ahoy_external_integration' as const;
const PROP_SIGNATURE = 'ahoy_email_signature' as const;

interface AccountSignatureEditorBodyProps {
  orgId: string;
  entityId: string;
  userId: number;
  canModify: boolean;
  integrationObject: CrmObject;
}

function AccountSignatureEditorBody({
  orgId,
  entityId,
  userId,
  canModify,
  integrationObject,
}: AccountSignatureEditorBodyProps) {
  const savedSignature = useMemo<string>(() => {
    const raw = integrationObject.properties?.[PROP_SIGNATURE]?.value;
    return typeof raw === 'string' ? raw : '';
  }, [integrationObject]);

  const [draft, setDraft] = useState<string>(() => savedSignature);
  const [serverError, setServerError] = useState<string | null>(null);
  const [isSaving, setIsSaving] = useState(false);

  // Adopt a newer server value when the query refetches, so Save writes on top
  // of current data rather than silently overwriting it. Only an untouched
  // draft is re-synced — an in-progress edit is never discarded, and stays
  // flagged dirty against the new saved value.
  const lastSyncedRef = useRef(savedSignature);
  useEffect(() => {
    if (savedSignature === lastSyncedRef.current) {
      return;
    }
    const previousSaved = lastSyncedRef.current;
    lastSyncedRef.current = savedSignature;
    setDraft(current => (current === previousSaved ? savedSignature : current));
  }, [savedSignature]);

  const updateProperty = useUpdateCrmProperty(parseInt(orgId), userId);

  const isDirty = draft !== savedSignature;

  const editorValue = useMemo(() => wrapSignatureForEditor(draft), [draft]);

  const handleDraftChange = useCallback((html: string) => {
    setDraft(unwrapSignatureFromEditor(html));
  }, []);

  // Persist the signature on explicit save. On a server rejection, roll the
  // draft back to the last saved value so a rejected write never looks saved.
  const handleSave = useCallback(() => {
    const previous = savedSignature;
    const next = draft.trim();
    setIsSaving(true);
    setServerError(null);
    updateProperty.mutate(
      {
        object: integrationObject,
        propertyName: PROP_SIGNATURE,
        value: next || null,
      },
      {
        onError: (err: unknown) => {
          setDraft(previous);
          setServerError("We couldn't save your signature. Try again.");
          logger.error(
            'Signature save failed',
            err instanceof Error ? err : new Error(String(err)),
            {
              component: 'integrations',
              property: PROP_SIGNATURE,
              entityId,
              errorType:
                err instanceof Error ? err.constructor.name : 'Unknown',
            }
          );
        },
        onSettled: () => setIsSaving(false),
      }
    );
  }, [draft, savedSignature, updateProperty, entityId, integrationObject]);

  return (
    <section aria-labelledby='signature-heading' className='space-y-1'>
      <p
        id='signature-heading'
        className='text-sm font-medium text-primary mb-1'
      >
        Email signature
      </p>
      <p className='text-xs text-tertiary mb-2'>
        Appended to emails Ahoy sends from this account when your Email
        Signature setting is on. Outlook doesn&apos;t let apps read the
        signature saved in Outlook itself, so set it here.
      </p>
      <div className='border border-border-secondary rounded-lg bg-bg-primary'>
        <PlateEmailEditor
          value={editorValue}
          onChange={handleDraftChange}
          placeholder='Best regards, your name'
          readOnly={!canModify || isSaving}
          orgId={parseInt(orgId)}
        />
      </div>
      {canModify && (
        <div className='flex justify-end'>
          <Button
            color='secondary'
            size='sm'
            onClick={handleSave}
            isDisabled={!isDirty || isSaving}
          >
            {isSaving ? 'Saving…' : 'Save signature'}
          </Button>
        </div>
      )}
      {serverError && (
        <p role='alert' className='text-xs text-error-600 mt-1'>
          {serverError}
        </p>
      )}
    </section>
  );
}

export default function AccountSignatureEditor({
  orgId,
  entityId,
  userId,
  canModify,
}: AccountSignatureEditorProps) {
  const {
    object: integrationObject,
    isLoading,
    error,
  } = useCrmObject(parseInt(orgId), ENTITY_TYPE, entityId);

  useEffect(() => {
    if (error) {
      logger.error(
        'Failed to load signature integration data',
        error instanceof Error ? error : new Error(String(error)),
        { entityId }
      );
    }
  }, [error, entityId]);

  if (isLoading) {
    return (
      <section>
        <LoadingIndicator size='sm' />
      </section>
    );
  }

  if (error) {
    return (
      <section>
        <p role='alert' className='text-xs text-error-600'>
          We couldn't load this data. Try again.
        </p>
      </section>
    );
  }

  if (!integrationObject) {
    return null;
  }

  return (
    <AccountSignatureEditorBody
      orgId={orgId}
      entityId={entityId}
      userId={userId}
      canModify={canModify}
      integrationObject={integrationObject}
    />
  );
}
